import MenuOriginal from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import React, { MouseEvent } from 'react';
import styled from 'styled-components';
import { EColors } from 'theme';
import { Row } from 'ui';

export type BookedState = Bizly.Venue['status'];

export const REJECTED_STATUSES = new Set<BookedState>([
    'Proposal Rejected',
    'Inquiry Rejected',
    'Inquiry Cancelled',
    'Proposal Cancelled',
    'Booking Cancelled',
    'Extension Rejected',
]);
/** Statuses that are not actively in-flight (not Submitted, Proposal Received, Contract Pending, Booked). Rejected + Added + Queued. */
export const NON_ACTIVE_STATUSES = new Set<BookedState>([...REJECTED_STATUSES, 'Added', 'Queued']);

export const bookedStateToBackgroundColor: { [key in BookedState]: EColors } = {
    Added: EColors.officePillColor, // this pill type shouldn't appear any more
    Queued: EColors.queuedVenueStatus,
    Submitted: EColors.submittedInquiryStatus,
    'Proposal Received': EColors.receivedProposalStatus,
    'Proposal Rejected': EColors.cancelledOrRejectedStatus,
    'Proposal Cancelled': EColors.cancelledOrRejectedStatus,
    'Extension Pending': EColors.contractPendingStatus,
    'Extension Rejected': EColors.cancelledOrRejectedStatus,
    'Proposal Expired': EColors.cancelledOrRejectedStatus,
    'Inquiry Rejected': EColors.cancelledOrRejectedStatus,
    'Inquiry Cancelled': EColors.cancelledOrRejectedStatus,
    'Contract Pending': EColors.contractPendingStatus,
    Booked: EColors.bookedVenueStatus,
    'Booking Cancelled': EColors.cancelledOrRejectedStatus,
};

export const VenueCardPopoverItem = ({
    children,
    ...props
}: {
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    children: React.ReactNode;
    disabled?: boolean;
}) => <MenuItem {...props}>{children}</MenuItem>;

const VenueCardPopoverWrapper = styled.div`
    cursor: pointer;
    color: ${({ theme: { getColor, EColors } }) => getColor(EColors.pureWhite)};
`;

const Dot = styled.div`
    width: 4px;
    height: 4px;

    background-color: currentColor;
    border-radius: 50%;

    &:not(:last-child) {
        margin-right: 3px;
    }
`;

const MenuIconTarget = styled(Row)<{ alignBottom?: boolean; hovered?: boolean; color?: EColors }>`
    width: 24px;
    height: 24px;
    padding-bottom: 12px;
    transform: translateY(12px);

    justify-content: center;
    align-items: ${props => (props.alignBottom ? 'flex-end' : 'center')};

    color: ${({ color, theme: { getColor } }) => getColor(color || EColors.pureBlack)};

    &:hover {
        color: ${({ color, theme: { getColor } }) => color || getColor(EColors.primaryAction)};
    }
    transition: 0.2s all ease-in;

    ${({ hovered, theme: { getColor, EColors } }) => (hovered ? `color: ${getColor(EColors.primaryAction)}` : ``)}
`;
const MenuIcon = ({ hovered = false, color }: { hovered?: boolean; color?: EColors }) => (
    <MenuIconTarget hovered={hovered} color={color}>
        <Dot />
        <Dot />
        <Dot />
    </MenuIconTarget>
);

export type TMenuOption = { label: string; handler: () => void; disabled?: boolean };

export type TVenueCardPopover = {
    options: TMenuOption[];
    hovered?: boolean;
    color?: EColors;
};

export const VenueCardPopover = ({ options, hovered = false, color }: TVenueCardPopover) => {
    const [anchorEl, setAnchorEl] = React.useState<null | Element>(null);
    function handleClick(event: MouseEvent<HTMLElement>) {
        setAnchorEl(event.currentTarget as Element);
    }

    function handleClose() {
        setAnchorEl(null);
    }
    return (
        <VenueCardPopoverWrapper onClick={e => e.stopPropagation()}>
            <div aria-owns={anchorEl ? 'simple-menu' : undefined} aria-haspopup="true" onClick={handleClick}>
                <MenuIcon hovered={hovered} color={color} />
            </div>
            <MenuOriginal
                id="simple-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                disableAutoFocusItem
            >
                {options.map(({ label, handler, disabled }) => (
                    <VenueCardPopoverItem
                        key={label}
                        disabled={disabled}
                        onClick={() => {
                            handler();
                            handleClose();
                        }}
                    >
                        {label}
                    </VenueCardPopoverItem>
                ))}
            </MenuOriginal>
        </VenueCardPopoverWrapper>
    );
};
